import { z } from 'zod';
import { blankToNull } from './blankToNull';

// One row of a building's delivery list, as delivered on
// `GET /app_service/panel/v1/locations/<location_id>/deliveries`.
//
// This declares its OWN row schema instead of reusing `DeliverySchema`, for one field.
// `DeliverySchema` requires `shared_with_recipient`, and this read deliberately does not send
// it: the flag means «shared with THIS «Usuario»», and a building-wide list has no owner
// «Usuario» for it to be relative to. Reusing that schema would fail the parse and blank the
// whole list; relaxing it would weaken the «Usuario» list, where the flag is real data and a
// silent `false` would drop a delivery's shared indicator. So the key is mirrored `nullish`
// here and normalized to `false`.
//
// This transform used to yield the very same `Delivery` domain object the «Usuario» list
// produces, which is what let the shared row component render these rows unchanged. It no
// longer does: it yields a SUPERSET, `Delivery` plus the delivery's `recipient`. The two lists
// therefore stopped sharing one shape — the building-wide list is the one with no owner
// «Usuario», so it is the only one that shows whose parcel each row is. The row component still
// takes these rows (a superset is assignable), but it now renders two extra cells when, and
// only when, a list hands it a titular.
//
// `status` and `carrier` stay open strings, never enums: a new backend state must not break
// parsing. `carrier` is the display name only — the query is keyed by a carrier's integer id.
export const LocationDeliveryRowSchema = z
  .object({
    id: z.string().min(1),
    status: z.string(),
    // Nullable, unlike the «Usuario» list's: a `failed`/`cancelled` delivery never reached a
    // box and arrives with no stored date. It normalizes to '' because the shared `Delivery`
    // type declares `storedDate` a string, and widening that would reach into the «Usuario»
    // list; the date formatter renders an unparseable value as itself, so the cell reads empty.
    stored_date: z.string().nullable(),
    collected_date: z.string().nullable(),
    box_number: z.string().nullable(),
    tracking: z.string().nullish(),
    carrier: z.string().nullish(),
    locker_timezone: z.string(),
    shared_with_recipient: z.boolean().nullish(),
    // `nullish` on the OBJECT, not `nullable`: this endpoint is live in production WITHOUT the
    // key — the titular is delivered code that has not been deployed — so the real response
    // omits it entirely. `nullish` is what keeps that response parsing instead of blanking the
    // whole list, and it is why the two columns can ship before the deploy.
    //
    // `name` and `phone` are `nullish` again INSIDE the object, and that is not redundant.
    // Backend sends `recipient: null` when the delivery has no titular at all, but sends the
    // object with its `id` and the other two `null` when there IS a titular whose row could not
    // be read — a dangling id (an `IntegerField`, not a declared FK) or a deactivated «Usuario».
    // Two different absences; the schema has to admit both.
    //
    // `id` is a plain `number`: if the object travels, the id is there. It is `clientes.id`, the
    // same id the «Usuario» reads use, carried so the row can one day link to the ficha — no
    // consumer reads it yet, which is exactly why it stops at this schema and never reaches the
    // row component.
    recipient: z
      .object({
        id: z.number(),
        name: z.string().nullish(),
        phone: z.string().nullish(),
      })
      .nullish(),
    // Declared IDENTICALLY to the «Usuario» list's, down to the `nullish` on both levels — the
    // two lists pasting one building two ways is the drift this field exists to prevent. The
    // reasoning for each level lives on that schema, beside the field it governs.
    location: z.object({ address: z.string().nullish() }).nullish(),
  })
  .transform((d) => ({
    id: d.id,
    status: d.status,
    storedDate: d.stored_date ?? '',
    collectedDate: d.collected_date,
    boxNumber: d.box_number,
    tracking: d.tracking ?? null,
    carrier: d.carrier ?? null,
    lockerTimezone: d.locker_timezone,
    sharedWithRecipient: d.shared_with_recipient ?? false,
    // What `blankToNull` does NOT do is collapse the object itself. An absent `recipient` and an
    // explicit `null` are the same thing — no titular — but a PRESENT object with `name`/`phone`
    // at `null` is not: there the titular exists and its row could not be read, and the `id` is
    // the proof. The screen paints «—» for both, deliberately; the domain object keeps them
    // apart for whoever needs the difference.
    recipient: d.recipient
      ? {
          id: d.recipient.id,
          name: blankToNull(d.recipient.name),
          phone: blankToNull(d.recipient.phone),
        }
      : null,
    // Flattened, unlike `recipient` above it: one member means «no key» and «null» are the same
    // fact, so collapsing them leaves one representable absence instead of two spellings of it.
    locationAddress: blankToNull(d.location?.address),
  }));

// The filters the read takes as query params. `carrier` is a carrier's INTEGER id — the column
// behind it is an indexed FK, and the carrier name is never what a query is keyed by.
//
// `carrier` is OPTIONAL: absence is the wire form of «every carrier», so the param is omitted —
// hence `.optional()` and neither `.nullable()` nor a `0` sentinel. Delivered as requested
// (AS-042, CBX-11167) and verified against dev: an unfiltered read answers rows, not a 400.
//
// `days` is mandatory: the read has no default window and answers 400 without one. It stays a
// plain number rather than the closed set of ranges, which is decided at the URL and select
// boundaries in the app; narrowing it here would have this package reach up into one app's
// vocabulary. `box` is optional and matched exactly, never as a prefix — a blank or
// whitespace-only value is no filter at all rather than a filter nothing matches.
//
// `states` is optional the same way and for the same reason — absence is the wire form of «every
// state» — with one extra rule: it is absent OR non-empty, never `[]`. An empty array would be a
// second spelling of the same absence, and the one that survives all the way to a request filtering
// by nothing. `.min(1)` on the array and NOT `.nonempty()`: the latter infers the tuple
// `[string, ...string[]]` in Zod 3, which the app's plain `string[]` no longer satisfies. Same
// check at runtime, a type the producers can actually assign.
export const LocationDeliveriesQuerySchema = z.object({
  carrier: z.number().int().optional(),
  days: z.number(),
  box: z.string().optional(),
  states: z.array(z.string().min(1)).min(1).optional(),
});

// The list envelope. Its key is `deliveries`, not the `results` the search reads use, and it
// carries no cursor fields: everything matching the filters comes back in one answer, so
// `count` always equals the list's length — box filter or not.
export const LocationDeliveriesResponseSchema = z.object({
  count: z.number(),
  deliveries: z.array(LocationDeliveryRowSchema),
});

// Exported because the location list is no longer renderable through `Delivery` alone: its
// layout has to name the row it hands to the shared component, and a `z.infer` at the call site
// would tie a view to this schema.
export type LocationDeliveryRow = z.infer<typeof LocationDeliveryRowSchema>;
export type LocationDeliveriesQuery = z.infer<typeof LocationDeliveriesQuerySchema>;
export type LocationDeliveriesResponse = z.infer<typeof LocationDeliveriesResponseSchema>;
